import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import {
    Button,
    TableCellLayout,
    TableColumnDefinition,
    TableColumnSizingOptions,
    Tooltip,
    createTableColumn,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import {
    bundleIcon,
    Delete24Filled,
    Delete24Regular,
    Edit24Filled,
    Edit24Regular,
} from '@fluentui/react-icons';
import { useAppDispatch, useSelector } from '../../../../store/hooks';
import {
    loadAutoReplies,
    selectAutoReplies,
    updateAutoReplies,
    searchAutoReplies,
} from '../../../../store/features/autoReplies/autoRepliesSlice';

import CreateOrUpdateResponseDialogAction from './components/UpdateAutoRepliesDialogAction';
import ConfirmDeletionDialog from '../../components/ConfirmDeletionDialog/ConfirmDeletionDialog';
import { AutoReplyMessageType } from '../../../../model/AutoReplyMessageType';
import { useDebouncedCallback } from 'use-debounce';
import { RootState } from '../../../../store';
import { selectAppConfigurationSearchQuery } from '../../../../store/features/appConfiguration/appConfigurationSlice';
import ConfigurationTable from '../../components/ConfigurationTable/ConfigurationTable';
import { IConfigRow } from '../../components/ConfigurationTable/ConfigurationTable.utils';

interface AutoReplyRow extends IConfigRow {
    value: string;
    title: string;
    initialReply: string;
    closedConnectionReply: string;
}

const EditIconBundle = bundleIcon(Edit24Filled, Edit24Regular);
const DeleteIconBundle = bundleIcon(Delete24Filled, Delete24Regular);

const AutoRepliesPanel = () => {
    const { t } = useTranslation();
    const dataGridRef = useRef(null);

    const dispatch = useAppDispatch();
    const { items, loadStatus, nextPageToken, pagination } =
        useSelector(selectAutoReplies);

    const searchQuery = useSelector(selectAppConfigurationSearchQuery);

    const [open, setOpen] = useState(false);
    const [selectedGroundTeam, setSelectedGroundTeam] = useState<string>('');
    const [selectedGroundTeamAutoReply, setSelectedGroundTeamAutoReply] =
        useState<string>('');
    const [editingMessageType, setEditingMessageType] =
        useState<AutoReplyMessageType>(AutoReplyMessageType.InitialReply);
    const [deletionTarget, setDeletionTarget] = useState<{
        item: AutoReplyRow;
        messageType: AutoReplyMessageType;
    } | null>(null);

    const resetDialogState = useCallback((open: boolean) => {
        setOpen(open);
        setSelectedGroundTeam('');
        setSelectedGroundTeamAutoReply('');
        setEditingMessageType(AutoReplyMessageType.InitialReply);
    }, []);

    const closeDeletionDialog = useCallback(() => {
        setDeletionTarget(null);
    }, []);

    useEffect(() => {
        dispatch(loadAutoReplies());
    }, [dispatch]);

    useEffect(() => {
        dispatch(searchAutoReplies(searchQuery));
    }, [dispatch, searchQuery]);

    const onEditInitialReplyCallback = useCallback((item: AutoReplyRow) => {
        setSelectedGroundTeam(item.id);
        setSelectedGroundTeamAutoReply(item.initialReply);
        setEditingMessageType(AutoReplyMessageType.InitialReply);
        setOpen(true);
    }, []);

    const onEditClosedConnectionReplyCallback = useCallback(
        (item: AutoReplyRow) => {
            setSelectedGroundTeam(item.id);
            setSelectedGroundTeamAutoReply(item.closedConnectionReply);
            setEditingMessageType(AutoReplyMessageType.ClosedConnectionReply);
            setOpen(true);
        },
        []
    );

    const onClearInitialReplyCallback = useCallback(
        (item: AutoReplyRow) => {
            resetDialogState(false);
            dispatch(
                updateAutoReplies({
                    rowKey: item.id,
                    autoReply: '',
                    messageType: AutoReplyMessageType.InitialReply,
                })
            );
        },
        [dispatch, resetDialogState]
    );

    const openDeletionDialog = useCallback(
        (item: AutoReplyRow, messageType: AutoReplyMessageType) => {
            setDeletionTarget({ item, messageType });
        },
        []
    );

    const deletionMessage = useMemo(() => {
        if (!deletionTarget) {
            return '';
        }

        return t('ConfirmAutoReplyDeletionDialog_Content')
            .replace('{groundTeam}', deletionTarget.item.title)
            .replace(
                '{messageType}',
                deletionTarget.messageType === AutoReplyMessageType.InitialReply
                    ? t('MessageType_InitialAutoReply')
                    : t('MessageType_ClosedConnectionAutoReply')
            );
    }, [deletionTarget, t]);

    const onClearClosedConnectionReplyCallback = useCallback(
        (item: AutoReplyRow) => {
            resetDialogState(false);
            dispatch(
                updateAutoReplies({
                    rowKey: item.id,
                    closedConnectionReply: '',
                    messageType: AutoReplyMessageType.ClosedConnectionReply,
                })
            );
        },
        [dispatch, resetDialogState]
    );

    const onScrollCallback = useCallback(
        (ev: Event) => {
            const target = ev.target as HTMLDivElement;
            if (
                nextPageToken &&
                target.scrollTop + target.clientHeight * 6 > target.scrollHeight
            ) {
                dispatch(loadAutoReplies());
            }
        },
        [dispatch, nextPageToken]
    );

    const handleScrollDebounced = useDebouncedCallback(onScrollCallback, 400);

    useEffect(() => {
        const sectionElement = dataGridRef.current as HTMLDivElement | null;
        if (sectionElement) {
            sectionElement.addEventListener('scroll', handleScrollDebounced);
        }
        return () => {
            if (sectionElement) {
                sectionElement.removeEventListener(
                    'scroll',
                    handleScrollDebounced
                );
            }
        };
    }, [dispatch, handleScrollDebounced]);

    const configRows: AutoReplyRow[] = useMemo(
        () =>
            items.map((item) => ({
                id: item.key,
                value: `${item.teamName} - ${item.channelName}`,
                title: `${item.teamName} - ${item.channelName}`,
                initialReply: item.autoReply,
                closedConnectionReply: item.closedConnectionReply,
            })),
        [items]
    );

    const autoRepliesColumnSizing: TableColumnSizingOptions = {
        groundTeam: {
            minWidth: 150,
            idealWidth: 250,
        },
        initialReply: {
            minWidth: 200,
            idealWidth: 350,
        },
        initialReplyActions: {
            minWidth: 100,
            idealWidth: 100,
        },
        closedConnectionReply: {
            minWidth: 200,
            idealWidth: 350,
        },
        closedConnectionReplyActions: {
            minWidth: 100,
            idealWidth: 100,
        },
    };

    const columns = useMemo<TableColumnDefinition<AutoReplyRow>[]>(
        () => [
            createTableColumn<AutoReplyRow>({
                columnId: 'groundTeam',
                compare: (a, b) => {
                    return a.title.localeCompare(b.title);
                },
                renderHeaderCell: () => {
                    return t('TableHeader_GroundTeam');
                },
                renderCell: (item) => {
                    return item.title;
                },
            }),
            createTableColumn<AutoReplyRow>({
                columnId: 'initialReply',
                compare: (a, b) => {
                    return a.initialReply.localeCompare(b.initialReply);
                },
                renderHeaderCell: () => {
                    return (
                        <Tooltip
                            content={t('TableHeader_InitialAutoReply_Tooltip')}
                            relationship="description"
                        >
                            <span>{t('TableHeader_InitialAutoReply')}</span>
                        </Tooltip>
                    );
                },
                renderCell: (item) => {
                    return item.initialReply;
                },
            }),
            createTableColumn<AutoReplyRow>({
                columnId: 'initialReplyActions',
                renderHeaderCell: () => {
                    return <></>;
                },
                renderCell: (item) => {
                    return (
                        <TableCellLayout>
                            <Tooltip
                                content={t('Tooltip_Edit')}
                                relationship="label"
                            >
                                <Button
                                    appearance="transparent"
                                    icon={<EditIconBundle />}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onEditInitialReplyCallback(item);
                                    }}
                                />
                            </Tooltip>
                            <Tooltip
                                content={t('Tooltip_Delete')}
                                relationship="label"
                            >
                                <Button
                                    appearance="transparent"
                                    icon={<DeleteIconBundle />}
                                    onClick={() => {
                                        openDeletionDialog(item, AutoReplyMessageType.InitialReply);
                                    }}
                                />
                            </Tooltip>
                        </TableCellLayout>
                    );
                },
            }),
            createTableColumn<AutoReplyRow>({
                columnId: 'closedConnectionReply',
                compare: (a, b) => {
                    return a.closedConnectionReply.localeCompare(
                        b.closedConnectionReply
                    );
                },
                renderHeaderCell: () => {
                    return (
                        <Tooltip
                            content={t(
                                'TableHeader_ClosedConnectionAutoReply_Tooltip'
                            )}
                            relationship="description"
                        >
                            <span>
                                {t('TableHeader_ClosedConnectionAutoReply')}
                            </span>
                        </Tooltip>
                    );
                },
                renderCell: (item) => {
                    return item.closedConnectionReply;
                },
            }),
            createTableColumn<AutoReplyRow>({
                columnId: 'closedConnectionReplyActions',
                renderHeaderCell: () => {
                    return <></>;
                },
                renderCell: (item) => {
                    return (
                        <TableCellLayout>
                            <Tooltip
                                content={t('Tooltip_Edit')}
                                relationship="label"
                            >
                                <Button
                                    appearance="transparent"
                                    icon={<EditIconBundle />}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onEditClosedConnectionReplyCallback(
                                            item
                                        );
                                    }}
                                />
                            </Tooltip>
                            <Tooltip
                                content={t('Tooltip_Delete')}
                                relationship="label"
                            >
                                <Button
                                    appearance="transparent"
                                    icon={<DeleteIconBundle />}
                                    onClick={() => {
                                        openDeletionDialog(item, AutoReplyMessageType.ClosedConnectionReply);
                                    }}
                                />
                            </Tooltip>
                        </TableCellLayout>
                    );
                },
            }),
        ],
        [
            t,
            onEditInitialReplyCallback,
            onEditClosedConnectionReplyCallback,
            openDeletionDialog,
        ]
    );

    return (
        <>
            <ConfigurationTable<AutoReplyRow>
                items={configRows}
                columns={columns}
                columnSizingOptions={autoRepliesColumnSizing}
                ariaLabel="autoRepliesTable"
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    totalCount: pagination.totalCount,
                }}
                loadStatus={loadStatus === 'loading' ? 'loading' : 'idle'}
            />

            <CreateOrUpdateResponseDialogAction
                open={open}
                allAutoReplies={items}
                groundTeam={selectedGroundTeam}
                autoReply={selectedGroundTeamAutoReply}
                messageType={editingMessageType}
                loadStatus={loadStatus}
                onOpenChange={(_, data) => {
                    resetDialogState(data.open);
                }}
                onSaveBtnClick={(e, { groundTeam, autoReply }) => {
                    resetDialogState(false);
                    dispatch(
                        updateAutoReplies({
                            rowKey: groundTeam,
                            autoReply:
                                editingMessageType ===
                                AutoReplyMessageType.InitialReply
                                    ? autoReply
                                    : undefined,
                            closedConnectionReply:
                                editingMessageType ===
                                AutoReplyMessageType.ClosedConnectionReply
                                    ? autoReply
                                    : undefined,
                            messageType: editingMessageType,
                        })
                    );
                }}
            />

            <ConfirmDeletionDialog
                open={!!deletionTarget}
                message={deletionMessage}
                onConfirm={() => {
                    if (!deletionTarget) {
                        return;
                    }

                    if (deletionTarget.messageType === AutoReplyMessageType.InitialReply) {
                        onClearInitialReplyCallback(deletionTarget.item);
                    } else {
                        onClearClosedConnectionReplyCallback(deletionTarget.item);
                    }

                    closeDeletionDialog();
                }}
                onClose={closeDeletionDialog}
            />
        </>
    );
};

export const selectAutoRepliesPanel = (state: RootState) =>
    state.autoRepliesPanel;

export default AutoRepliesPanel;
